import React, { useEffect, useState } from "react";
import classNames from "classnames/bind";
import { InboxOutlined, PaperClipOutlined } from "@ant-design/icons";
import { Button, Modal, Upload, message } from "antd";
import CvFilePreview from "./CvFilePreview";
import {
  CERTIFICATE_ACCEPT_ATTR,
  CV_MAX_BYTES,
  executeRecruitmentCvUpload,
  getCvPreviewMode,
  isAllowedCertificateFile,
  isAllowedCvFile,
} from "../../../../utils/constants/uploadCvUtils";
import styles from "./upload.module.sass";

const cx = classNames.bind(styles);
const { Dragger } = Upload;

const UploadCvModal = ({
  open,
  onClose,
  campaignId,
  candidateId,
  uploadContext,
  onSuccess,
}) => {
  const [fileList, setFileList] = useState([]);
  const [certificateFileList, setCertificateFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const selectedFile = fileList[0]?.originFileObj ?? null;
  const hasPdfPreview =
    Boolean(selectedFile && previewUrl) &&
    getCvPreviewMode(selectedFile) === "pdf";

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const resetState = () => {
    setFileList([]);
    setCertificateFileList([]);
    setPreviewUrl(null);
    setUploading(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose?.();
  };

  const handleBeforeUpload = (file) => {
    if (!isAllowedCvFile(file)) {
      message.error("Chỉ chấp nhận file PDF, DOC, DOCX");
      return Upload.LIST_IGNORE;
    }

    if (file.size > CV_MAX_BYTES) {
      message.error("File vượt quá 50MB");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleBeforeCertificateUpload = (file) => {
    if (!isAllowedCertificateFile(file)) {
      message.error(
        "Chỉ chấp nhận PDF, DOC, DOCX hoặc ảnh (JPG, PNG, GIF, WEBP, BMP)",
      );
      return Upload.LIST_IGNORE;
    }

    if (file.size > CV_MAX_BYTES) {
      message.error("File vượt quá 50MB");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChangeFile = () => {
    if (uploading) return;
    setFileList([]);
  };

  const handleUpload = async () => {
    if (!campaignId) {
      message.error("Thiếu thông tin chiến dịch. Vui lòng tải lại trang.");
      return;
    }

    if (!uploadContext?.userId || !uploadContext?.session) {
      message.error("Thiếu thông tin phiên chat. Vui lòng tải lại trang.");
      return;
    }

    const certificateFiles = certificateFileList
      .map((item) => item.originFileObj)
      .filter(Boolean);

    setUploading(true);
    try {
      const uploadResult = await executeRecruitmentCvUpload({
        file: selectedFile,
        certificateFiles,
        campaignId,
        candidateId,
        uploadContext,
      });

      const hasCertificates = certificateFiles.length > 0;
      message.success(
        selectedFile && hasCertificates
          ? "Tải CV và chứng chỉ thành công"
          : selectedFile
            ? "Tải CV thành công"
            : hasCertificates
              ? "Tải chứng chỉ thành công"
              : "Ứng tuyển thành công",
      );
      onSuccess?.(uploadResult);
      resetState();
      onClose?.();
    } catch (error) {
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      message.error(apiError || "Tải file thất bại");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={hasPdfPreview ? 680 : 520}
      className={cx("uploadCvModal")}
      title="Tải CV / tài liệu ứng tuyển"
      closable={!uploading}
    >
      <div className={cx("uploadCvModalBody")}>
        <section className={cx("uploadCvCvSection")}>
          <p className={cx("uploadCvSectionLabel")}>
            CV / hồ sơ ứng tuyển
          </p>
          {selectedFile ? (
            <CvFilePreview
              file={selectedFile}
              previewUrl={previewUrl}
              onChangeFile={handleChangeFile}
              disabled={uploading}
            />
          ) : (
            <Dragger
              className={cx("uploadCvCvDragger")}
              accept=".pdf,.doc,.docx"
              showUploadList={false}
              maxCount={1}
              disabled={uploading}
              fileList={fileList}
              beforeUpload={handleBeforeUpload}
              onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Kéo thả hoặc bấm để chọn file CV</p>
              <p className="ant-upload-hint">PDF, DOC, DOCX — tối đa 50MB</p>
            </Dragger>
          )}
        </section>

        <section className={cx("uploadCvCertificates")}>
          <p className={cx("uploadCvSectionLabel")}>
            Chứng chỉ liên quan
          </p>
          <Upload
            className={cx("uploadCvCertUpload")}
            accept={CERTIFICATE_ACCEPT_ATTR}
            multiple
            disabled={uploading}
            fileList={certificateFileList}
            beforeUpload={handleBeforeCertificateUpload}
            onChange={({ fileList: nextFileList }) =>
              setCertificateFileList(nextFileList)
            }
            showUploadList={{
              showPreviewIcon: false,
              showDownloadIcon: false,
              showRemoveIcon: !uploading,
            }}
            listType="text"
          >
            <Button
              icon={<PaperClipOutlined />}
              disabled={uploading}
              className={cx("uploadCvCertAddBtn")}
            >
              Thêm chứng chỉ
            </Button>
          </Upload>
          <p className={cx("uploadCvCertHint")}>
            Có thể chọn nhiều file · PDF, DOC, DOCX, JPG, PNG — tối đa 50MB/file
          </p>
        </section>
      </div>

      <div className={cx("uploadCvActions")}>
        <Button onClick={handleClose} disabled={uploading}>
          Hủy
        </Button>
        <Button
          type="primary"
          onClick={handleUpload}
          loading={uploading}
        >
          Ứng tuyển ngay
        </Button>
      </div>
    </Modal>
  );
};

export default UploadCvModal;
